import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { AppQueryClient } from "../../../api/queryClient";
import * as Library from "../../../utils/Library";
import CardGridSkeleton from "../gridCards/animations/CardGridSkeleton";

// #region SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 17px;
    }
`;
// #endregion

/**
 * Standard infinite card grid.
 *
 * Required props:
 * - pageCard: card component that accepts data, emptyCard, className and the
 *   three standard button callbacks.
 * - queryKey: React Query key for this grid.
 * - minColumnWidth, imageHeight and skeletonHeight: CSS dimensions.
 * - endpointName: endpoint name, optionally in "BOCommonContext/operation" form.
 * - payload: request body containing a positive limit.
 *
 * Optional props:
 * - className.
 * - requestEnabled (default true).
 * - showEmptyCardWithData (default true).
 * - getItemKey(item, itemIndex, pageIndex).
 * - primaryButtonOnClick, secondaryButtonOnClick and thirdButtonOnClick.
 *
 * Imperative API:
 * - ref.reset(): scrolls to the top and refreshes only this grid query.
 */
export default forwardRef(function InfinityGrid(props, ref) {
    // #region CONSTANTS & CONFIG
    const {
        // Mandatory
        pageCard: PageCard,
        queryKey: queryKeyProp,
        minColumnWidth,
        imageHeight,
        skeletonHeight,
        endpointName,
        payload,

        // Optional
        className,
        requestEnabled = true,
        showEmptyCardWithData = true,
        getItemKey,
        primaryButtonOnClick,
        secondaryButtonOnClick,
        thirdButtonOnClick
    } = props || {};

    if (
        !PageCard ||
        !queryKeyProp ||
        !minColumnWidth ||
        !imageHeight ||
        !skeletonHeight ||
        !endpointName ||
        !payload ||
        !payload?.limit
    ) {
        console.error("ERROR: InfinityGrid missing props...");
    }

    const pageLimit = payload?.limit || 0;
    const fallbackQueryKey = useRef(["InfinityGrid", uuidv4()]);
    const [refreshID, setRefreshID] = useState(null);
    const baseQueryKey = Array.isArray(queryKeyProp)
        ? queryKeyProp
        : queryKeyProp
          ? [queryKeyProp]
          : fallbackQueryKey.current;
    const queryKey = refreshID ? [...baseQueryKey, { refreshID }] : baseQueryKey;

    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`,
        gap: "24px"
    };
    // #endregion

    // #region STATE & REFS
    const scrollContainerRef = useRef(null);
    const [canRequestNextPage, setCanRequestNextPage] = useState(true);
    // #endregion

    // #region SERVER STATE
    const endpointParts = endpointName?.split("/") || [];
    const useBOCommon = endpointParts.length > 1;
    const endpointFolder = useBOCommon ? endpointParts[0] : undefined;
    const endpointNameToCall = useBOCommon ? endpointParts.slice(1).join("/") : endpointName;

    const {
        data,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = Library.usePostInfiniteQuery(
        queryKey,
        endpointNameToCall,
        payload,
        () => setCanRequestNextPage(true),
        useBOCommon,
        endpointFolder,
        false,
        requestEnabled
    );
    // #endregion

    // #region COMPUTED PROPS
    const pageList = Array.isArray(data?.pages) ? data.pages : [];
    const lastPageData = pageList[pageList.length - 1]?.data;
    const parsedNextDataCount = Number(lastPageData?.nextDataCount);
    const nextDataCount =
        Number.isFinite(parsedNextDataCount) && parsedNextDataCount >= 0 ? parsedNextDataCount : pageLimit;
    const isInitialLoading = isFetching && !pageList.length;

    const cards = useMemo(() => {
        return pageList.flatMap((page, pageIndex) => {
            const pageItems = Array.isArray(page?.data?.result) ? page.data.result : [];

            return pageItems.map((item, itemIndex) => ({
                item,
                itemIndex,
                pageIndex
            }));
        });
    }, [pageList]);
    const shouldShowEmptyCard = !isFetchingNextPage && (showEmptyCardWithData || !cards.length);
    // #endregion

    // #region METHODS
    const handleScroll = useCallback(() => {
        const scrollContainer = scrollContainerRef.current;
        if (
            !scrollContainer ||
            isFetching ||
            isFetchingNextPage ||
            !hasNextPage ||
            !canRequestNextPage
        ) {
            return;
        }

        const hasScrollableContent = scrollContainer.scrollHeight > scrollContainer.clientHeight;
        const reachedLoadThreshold =
            !hasScrollableContent ||
            scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight * 0.8;

        if (!reachedLoadThreshold) return;

        setCanRequestNextPage(false);
        fetchNextPage();
    }, [canRequestNextPage, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage]);

    const handleReset = useCallback(() => {
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;

        setCanRequestNextPage(true);
        AppQueryClient.removeQueries(queryKey, { exact: true });
        setRefreshID(uuidv4());
    }, [queryKey]);
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        handleScroll();
    }, [cards.length, handleScroll]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer || isInitialLoading || isFetchingNextPage || !hasNextPage) return;

        scrollContainer.addEventListener("scroll", handleScroll);
        return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }, [handleScroll, hasNextPage, isFetchingNextPage, isInitialLoading]);

    useImperativeHandle(
        ref,
        () => ({
            reset: handleReset
        }),
        [handleReset]
    );
    // #endregion

    // #region RENDER
    return (
        <StyledPage className={`library-scrollable ${className || ""}`} ref={scrollContainerRef}>
            {isInitialLoading ? (
                <div style={gridLayoutStyle}>
                    {Array.from({ length: pageLimit }, (_, index) => (
                        <CardGridSkeleton
                            key={`infinity-grid-initial-skeleton-${index}`}
                            height={skeletonHeight}
                            minColumnWidth={minColumnWidth}
                            imageHeight={imageHeight}
                        />
                    ))}
                </div>
            ) : (
                <div className="mb-24px" style={gridLayoutStyle}>
                    {cards.map(({ item, itemIndex, pageIndex }) => {
                        const itemKey = getItemKey?.(item, itemIndex, pageIndex);

                        return (
                            <PageCard
                                key={itemKey ?? `${pageIndex}-${itemIndex}`}
                                className={className}
                                data={item}
                                emptyCard={false}
                                primaryButtonOnClick={primaryButtonOnClick}
                                secondaryButtonOnClick={secondaryButtonOnClick}
                                thirdButtonOnClick={thirdButtonOnClick}
                            />
                        );
                    })}

                    {shouldShowEmptyCard && (
                        <PageCard
                            key="infinity-grid-empty-card"
                            className={className}
                            data={{}}
                            emptyCard={true}
                        />
                    )}

                    {isFetchingNextPage &&
                        Array.from({ length: nextDataCount }, (_, index) => (
                            <CardGridSkeleton
                                key={`infinity-grid-next-skeleton-${index}`}
                                height={skeletonHeight}
                                minColumnWidth={minColumnWidth}
                                imageHeight={imageHeight}
                            />
                        ))}
                </div>
            )}
        </StyledPage>
    );
    // #endregion
});
